import {
  Button,
  Checkbox,
  Circle,
  CloseButton,
  Drawer,
  Field,
  Flex,
  Float,
  Input,
  NumberInput,
  Portal,
  Select,
  Text,
} from '@chakra-ui/react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MdFilterAlt } from 'react-icons/md';
import { RiResetLeftFill } from 'react-icons/ri';

import SelectUnits from '@/components/common/SelectUnits';
import { category } from '@/components/indicators/index/IndicatorData';
import { Tooltip } from '@/components/ui/tooltip';
import useMediaQuery from '@/hooks/use-media-query.ts';
import { Route } from '@/routes/_layout/$slug/indicators/collecte/$year/index.tsx';

const CollecteFilterDrawer = ({ filter }: { filter: string }) => {
  const { slug } = useParams({ strict: false });
  const navigate = useNavigate({ from: Route.fullPath });
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const { t } = useTranslation();
  const defaultFilters = {
    indicator_year_setting___indicator___commitment___pillar___name__ilike: '',
    indicator_year_setting___indicator___commitment___name__ilike: '',
    indicator_year_setting___indicator___name__ilike: '',
    indicator_year_setting___indicator___category__in: [],
    indicator_year_setting___indicator___general_data__exact: null,
    indicator_year_setting___indicator___unit__in: [],
    value__ge: undefined,
    value__le: undefined,
    objective__ge: undefined,
    objective__le: undefined,
  };
  const [filters, setFilters] = useState(() => ({ ...defaultFilters }));
  const { numberOfFilterActive } = useMemo(() => {
    const isActive = (value: any) => {
      if (Array.isArray(value)) return value.length > 0;
      return value !== '' && value !== null && value !== undefined;
    };

    const activeCount = Object.values(filters).filter(isActive).length;

    return { numberOfFilterActive: activeCount };
  }, [filters]);
  const [resetTrigger, setResetTrigger] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (filter) {
      const parsedFilters: { [key: string]: any } = {};

      // Split the filter parameter (e.g., name=task&priority=high)
      const filterParams = new URLSearchParams(filter);

      // Populate the filters state with the values from the query parameter
      filterParams.forEach((value, key) => {
        if (key.endsWith('__in')) {
          parsedFilters[key] = value
            .split(',')
            .map((v) => v.trim())
            .filter((v) => v !== '');
        } else if (key in filters) {
          const lower = value.toLowerCase();
          switch (lower) {
            case 'true':
              parsedFilters[key] = true;
              break;
            case 'false':
              parsedFilters[key] = false;
              break;
            default:
              if (key.endsWith('__ilike')) parsedFilters[key] = value.replace(/^%|%$/g, '');
              else parsedFilters[key] = value;
          }
        }
      });

      // Update the filters state
      setFilters((prev) => ({
        ...prev,
        ...parsedFilters,
      }));
    }
  }, [filter]);

  useEffect(() => {
    if (resetTrigger) {
      applyFilters();
      setResetTrigger(false);
    }
  }, [resetTrigger]);

  const handleFilterChange = (column: string, value: any) => {
    setFilters((prev) => ({ ...prev, [column]: value }));
  };

  const applyFilters = () => {
    const cleanedFilters = Object.fromEntries(
      Object.entries(filters)
        .filter(
          ([_, value]) =>
            value !== null &&
            value !== undefined &&
            value !== '' &&
            !(Array.isArray(value) && (value.length === 0 || (value.length === 1 && value[0] === '')))
        )
        .map(([key, value]) => {
          let stringValue = Array.isArray(value) ? value.join(',') : String(value);

          if (key.endsWith('__ilike')) {
            stringValue = `%${stringValue}%`;
          } else if (key.endsWith('__in')) {
            if (!stringValue.includes(',')) {
              stringValue = `${stringValue},`;
            }
          }

          return [key, stringValue];
        })
    );

    const encodedFilter = new URLSearchParams(cleanedFilters).toString();
    navigate({
      search: (prev: Record<string, string>) => ({
        ...prev,
        filter: encodedFilter.replace(/\+/g, '%20'),
      }),
    });
    setIsDrawerOpen(false);
  };

  const resetFilters = () => {
    setFilters({ ...defaultFilters });
    setResetTrigger(true);
  };

  useEffect(() => {
    if (isDrawerOpen) {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Enter') {
          applyFilters();
        }
      };

      window.addEventListener('keydown', handleKeyDown);

      return () => {
        window.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isDrawerOpen, filters]);

  const isTooltipHidden = useMediaQuery('(min-width: 1250px)');

  return (
    <Drawer.Root
      open={isDrawerOpen}
      onOpenChange={(e) => setIsDrawerOpen(e.open)}
      size="sm"
      placement="end"
      onEscapeKeyDown={() => applyFilters()}
    >
      <Drawer.Trigger as="div">
        <Tooltip
          showArrow
          content={t('ui.filter')}
          openDelay={100}
          closeDelay={100}
          contentProps={{ css: { '--tooltip-bg': 'colors.primary' } }}
          disabled={isTooltipHidden}
        >
          <Button variant="outline" size="sm" {...(!isTooltipHidden && { p: '0' })}>
            <MdFilterAlt />
            {isTooltipHidden && <span>{t('ui.filter')}</span>}

            {numberOfFilterActive !== 0 && (
              <Float offsetY="1">
                <Circle size="4" bg="primary" color="white" fontSize={'xs'}>
                  {numberOfFilterActive}
                </Circle>
              </Float>
            )}
          </Button>
        </Tooltip>
      </Drawer.Trigger>
      <Portal>
        <Drawer.Backdrop position={'fixed'} />
        <Drawer.Content
          rounded="md"
          position={'fixed'}
          margin={'{spacing.4}'}
          h={'calc(100% - {spacing.4} * 2)'}
          right={0}
          top={0}
          ref={contentRef}
        >
          <Drawer.CloseTrigger asChild mt="2" mr="4">
            <CloseButton size="sm" />
          </Drawer.CloseTrigger>
          <Drawer.Header>
            <Text textStyle={'xl'} fontWeight={'bold'}>
              {t('ui.filter')}
            </Text>
          </Drawer.Header>
          <Drawer.Body>
            {/* Pillar Name Filter */}
            <Field.Root>
              <Field.Label>{t('indicator.filter.pillarName.contains')}</Field.Label>
              <Input
                placeholder={t('ui.search')}
                value={filters.indicator_year_setting___indicator___commitment___pillar___name__ilike}
                onChange={(e) =>
                  handleFilterChange(
                    'indicator_year_setting___indicator___commitment___pillar___name__ilike',
                    e.target.value
                  )
                }
              />
            </Field.Root>

            {/* Commitment Name Filter */}
            <Field.Root mt={2}>
              <Field.Label>{t('indicator.filter.commitmentName.contains')}</Field.Label>
              <Input
                placeholder={t('ui.search')}
                value={filters.indicator_year_setting___indicator___commitment___name__ilike}
                onChange={(e) =>
                  handleFilterChange('indicator_year_setting___indicator___commitment___name__ilike', e.target.value)
                }
              />
            </Field.Root>

            {/* General Data Filter */}
            <Field.Root mt={2}>
              <Field.Label />
              <Checkbox.Root
                checked={
                  filters.indicator_year_setting___indicator___general_data__exact == null
                    ? 'indeterminate'
                    : filters.indicator_year_setting___indicator___general_data__exact
                }
                onCheckedChange={() => {
                  const newValue =
                    filters.indicator_year_setting___indicator___general_data__exact == null
                      ? true
                      : filters.indicator_year_setting___indicator___general_data__exact === true
                        ? false
                        : null;
                  handleFilterChange('indicator_year_setting___indicator___general_data__exact', newValue);
                }}
              >
                <Checkbox.HiddenInput />
                <Checkbox.Control>
                  <Checkbox.Indicator />
                </Checkbox.Control>
                <Checkbox.Label>{t('indicator.filter.generalData')}</Checkbox.Label>
              </Checkbox.Root>
            </Field.Root>

            {/* Name Filter */}
            <Field.Root mt={2}>
              <Field.Label>{t('indicator.filter.indicatorName.contains')}</Field.Label>
              <Input
                placeholder={t('ui.search')}
                value={filters.indicator_year_setting___indicator___name__ilike}
                onChange={(e) => handleFilterChange('indicator_year_setting___indicator___name__ilike', e.target.value)}
              />
            </Field.Root>

            {/* Category Filter */}
            <Field.Root mt={2}>
              <Select.Root
                collection={category}
                size="sm"
                value={filters.indicator_year_setting___indicator___category__in}
                onValueChange={(e) => handleFilterChange('indicator_year_setting___indicator___category__in', e.value)}
              >
                <Select.Label>{t('indicator.filter.category.select')}</Select.Label>
                <Select.Control>
                  <Select.Trigger>
                    <Select.ValueText placeholder={t('indicator.filter.category.select')} />
                  </Select.Trigger>
                  <Select.IndicatorGroup>
                    <Select.ClearTrigger />
                    <Select.Indicator />
                  </Select.IndicatorGroup>
                </Select.Control>
                <Portal container={contentRef}>
                  <Select.Positioner>
                    <Select.Content>
                      {category.items.map((el) => (
                        <Select.Item item={el} key={el.value}>
                          {el.label}
                          <Select.ItemIndicator />
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Positioner>
                </Portal>
              </Select.Root>
            </Field.Root>

            {/* Unit Filter */}
            <Field.Root mt={2}>
              <Field.Label>{t('indicator.filter.unit.contains')}</Field.Label>
              <SelectUnits
                slug={slug}
                value={filters.indicator_year_setting___indicator___unit__in}
                onChange={(e) => handleFilterChange('indicator_year_setting___indicator___unit__in', e)}
              />
            </Field.Root>

            {/* Value Filter */}
            <Field.Root mt={2}>
              <Field.Label>{t('collection.filter.valueBetween')}</Field.Label>
            </Field.Root>
            <Flex gap={2} mt={2}>
              <Field.Root mt={2}>
                <NumberInput.Root
                  size="sm"
                  value={filters.value__ge}
                  onValueChange={(e) => handleFilterChange('value__ge', e.value)}
                >
                  <NumberInput.Control />
                  <NumberInput.Input placeholder="Min" />
                </NumberInput.Root>
              </Field.Root>
              <Field.Root mt={2}>
                <NumberInput.Root
                  size="sm"
                  value={filters.value__le}
                  onValueChange={(e) => handleFilterChange('value__le', e.value)}
                >
                  <NumberInput.Control />
                  <NumberInput.Input placeholder="Max" />
                </NumberInput.Root>
              </Field.Root>
            </Flex>

            {/* objective Filter */}
            <Field.Root mt={2}>
              <Field.Label>{t('collection.filter.objectiveBetween')}</Field.Label>
            </Field.Root>
            <Flex gap={2} mt={2}>
              <Field.Root mt={2}>
                <NumberInput.Root
                  size="sm"
                  value={filters.objective__ge}
                  onValueChange={(e) => handleFilterChange('objective__ge', e.value)}
                >
                  <NumberInput.Control />
                  <NumberInput.Input placeholder="Max" />
                </NumberInput.Root>
              </Field.Root>
              <Field.Root mt={2}>
                <NumberInput.Root
                  size="sm"
                  value={filters.objective__le}
                  onValueChange={(e) => handleFilterChange('objective__le', e.value)}
                >
                  <NumberInput.Control />
                  <NumberInput.Input placeholder="Max" />
                </NumberInput.Root>
              </Field.Root>
            </Flex>
          </Drawer.Body>

          <Drawer.Footer>
            <Button variant="outline" mr={'auto'} onClick={resetFilters}>
              <RiResetLeftFill />
              {t('ui.reset')}
            </Button>
            <Button variant="outline" mr={3} onClick={() => setIsDrawerOpen(false)}>
              {t('ui.cancel')}
            </Button>
            <Button colorScheme="blue" onClick={applyFilters}>
              {t('ui.apply')}
            </Button>
          </Drawer.Footer>
        </Drawer.Content>
      </Portal>
    </Drawer.Root>
  );
};

export default CollecteFilterDrawer;
